import { supabase } from '../supabaseClient';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { getRankInfo } from '../rank';

const API = process.env.REACT_APP_API_URL || '';

// ─── Icons ────────────────────────────────────────────────────────────────────

function MoonIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
      <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"/>
    </svg>
  );
}
function SunIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
      <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd"/>
    </svg>
  );
}
function EyeIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <path d="M1 10s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z"/>
      <circle cx="10" cy="10" r="2.5"/>
    </svg>
  );
}
function EyeOffIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <path d="M13.875 13.875A8.966 8.966 0 0 1 10 15c-5.5 0-9-5-9-5a16.4 16.4 0 0 1 4.125-4.125M8.1 4.2A8.6 8.6 0 0 1 10 4c5.5 0 9 6 9 6a16.4 16.4 0 0 1-2.1 2.9M6.35 6.35A4 4 0 0 0 10 14a4 4 0 0 0 3.65-7.65"/>
      <line x1="2" y1="2" x2="18" y2="18"/>
    </svg>
  );
}

// ─── Demo Data ────────────────────────────────────────────────────────────────

const DEMO_QUESTIONS = [
  // ── R&W (indices 0–5) ──────────────────────────────────────────────────────
  {
    section: 'Reading & Writing',
    domain: 'Craft and Structure',
    skill: 'Words in Context',
    passage: "Ezra Pound's poetry can be hard to ______: it is dense, experimental, and so full of references and allusions that many readers have a difficult time even identifying the poems' subjects.",
    question: 'Which choice completes the text with the most logical and precise word or phrase?',
    choices: { A: 'comprehend', B: 'dislike', C: 'interrupt', D: 'overlook' },
    correctAnswer: 'A',
  },
  {
    section: 'Reading & Writing',
    domain: 'Craft and Structure',
    skill: 'Words in Context',
    passage: 'The unique subak water management system used to irrigate the rice paddy fields of the Indonesian island of Bali has a rich cultural, philosophical, and historical significance dating back to the ninth century. The many elements of subak—terraces, canals, and water temples—are ______: they are joined together into a single cohesive unit.',
    question: 'Which choice completes the text with the most logical and precise word or phrase?',
    choices: { A: 'outmoded', B: 'informal', C: 'interconnected', D: 'optional' },
    correctAnswer: 'C',
  },
  {
    section: 'Reading & Writing',
    domain: 'Craft and Structure',
    skill: 'Words in Context',
    passage: "Although the government of the Soviet Union attempted to ______ Georgi Vladimov's novel Faithful Ruslan, copies of the book circulated in secret among readers in several parts of the country.",
    question: 'Which choice completes the text with the most logical and precise word or phrase?',
    choices: { A: 'replicate', B: 'critique', C: 'import', D: 'suppress' },
    correctAnswer: 'D',
  },
  {
    section: 'Reading & Writing',
    domain: 'Craft and Structure',
    skill: 'Words in Context',
    passage: 'Scholars long thought that the initial spread of silk beyond China occurred in the second century CE, but this view has been ______ by new archaeological evidence from South Asia that reveals that the people of the Indus Civilization made use of silk at least 1,000 years earlier.',
    question: 'Which choice completes the text with the most logical and precise word or phrase?',
    choices: { A: 'investigated', B: 'misinterpreted', C: 'anticipated', D: 'contradicted' },
    correctAnswer: 'D',
  },
  {
    section: 'Reading & Writing',
    domain: 'Craft and Structure',
    skill: 'Words in Context',
    passage: 'One challenge of generating electricity from ocean waves is that wave power isn\'t ______: it varies in unpredictable ways that pose technological and planning problems for electricity generation.',
    question: 'Which choice completes the text with the most logical and precise word or phrase?',
    choices: { A: 'accidental', B: 'confident', C: 'expensive', D: 'consistent' },
    correctAnswer: 'D',
  },
  {
    section: 'Reading & Writing',
    domain: 'Craft and Structure',
    skill: 'Words in Context',
    passage: 'Virginia Woolf\'s stream-of-consciousness style can be difficult to ______: her sentences spiral between multiple characters\' perspectives without clear transitions, leaving many readers uncertain of who is speaking at any given moment.',
    question: 'Which choice completes the text with the most logical and precise word or phrase?',
    choices: { A: 'enjoy', B: 'translate', C: 'follow', D: 'publish' },
    correctAnswer: 'C',
  },
  // ── Math (indices 6–11) ────────────────────────────────────────────────────
  {
    section: 'Math',
    domain: 'Algebra',
    skill: 'Algebra',
    passage: '4x + 1 = 33',
    question: 'Which equation has the same solution as the given equation?',
    choices: { A: '4x = 32', B: '4x = 5', C: '4x = 1', D: '4x = −32' },
    correctAnswer: 'A',
  },
  {
    section: 'Math',
    domain: 'Geometry and Trigonometry',
    skill: 'Coordinate Plane',
    passage: 'For the linear function f, the graph of y = f(x) in the xy-plane has a slope of 7 and passes through the point (0, 5).',
    question: 'Which equation defines f?',
    choices: { A: 'f(x) = 5x', B: 'f(x) = 35x', C: 'f(x) = 7x + 5', D: 'f(x) = 12x + 5' },
    correctAnswer: 'C',
  },
  {
    section: 'Math',
    domain: 'Advanced Math',
    skill: 'Powers and Roots',
    passage: 'The function g is defined by g(x) = √x + 300.',
    question: 'What is the value of g(x) when x = 81?',
    choices: { A: '9', B: '300', C: '309', D: '381' },
    correctAnswer: 'C',
  },
  {
    section: 'Math',
    domain: 'Problem-Solving and Data Analysis',
    skill: 'Statistics',
    passage: 'A cable provider wanted to know how many of its 30,000 customers would be interested in a new service plan. The provider selected 300 customers at random and asked each customer whether they would be interested. Of those surveyed, 8 said they would be interested.',
    question: 'Which of the following is the best estimate of the total number of customers who would be interested in the new service plan?',
    choices: { A: '8', B: '80', C: '800', D: '8,000' },
    correctAnswer: 'C',
  },
  {
    section: 'Math',
    domain: 'Advanced Math',
    skill: 'Advanced Math',
    passage: '64t²s³ − 56t³s',
    question: 'Which expression is equivalent to 64t²s³ − 56t³s?',
    choices: {
      A: '4ts(16s² − 14ts)',
      B: '4ts(16t²s² − 14t)',
      C: '4t²s(16ts − 14s)',
      D: '4t²s(16s² − 14t)',
    },
    correctAnswer: 'D',
  },
  {
    section: 'Math',
    domain: 'Advanced Math',
    skill: 'Advanced Math',
    passage: 'x + 5 = 14\ny = 4x² + 4',
    question: 'At what point (x, y) do the graphs of the equations in the given system intersect?',
    choices: { A: '(9, 324)', B: '(9, 328)', C: '(14, 4)', D: '(14, 788)' },
    correctAnswer: 'B',
  },
];

// Fixed demo bots
const PLAYER_BOT = { name: 'maya_s',   school: 'Phillips Exeter', rating: 12000, initials: 'M' };
const OPP_BOT    = { name: 'j_torres', school: 'Lincoln High',    rating: 1000,  initials: 'J' };

// Q1–Q4 already answered before demo starts (indices 0–3)
const PRE_ANSWERS = {
  0: { letter: 'C', correct: false }, // Q1 wrong  (correct: A)
  1: { letter: 'C', correct: true  }, // Q2 correct (correct: C)
  2: { letter: 'B', correct: false }, // Q3 wrong  (correct: D)
  3: { letter: 'A', correct: false }, // Q4 wrong  (correct: D)
};
const OPP_PRE_ANSWERS = {
  0: { letter: 'B', correct: false },
  1: { letter: 'D', correct: false },
  2: { letter: 'A', correct: false },
  3: { letter: 'B', correct: false },
};

// Player answers Q5–Q12 at 7-second intervals (4/12 correct = 33%)
const PLAYER_ACTIONS = [
  { idx: 4,  time: 7000,  letter: 'A', correct: false }, // Q5  wrong  (correct: D)
  { idx: 5,  time: 14000, letter: 'B', correct: false }, // Q6  wrong  (correct: C)
  { idx: 6,  time: 21000, letter: 'A', correct: true  }, // Q7  correct(correct: A)
  { idx: 7,  time: 28000, letter: 'B', correct: false }, // Q8  wrong  (correct: C)
  { idx: 8,  time: 35000, letter: 'A', correct: false }, // Q9  wrong  (correct: C)
  { idx: 9,  time: 42000, letter: 'C', correct: true  }, // Q10 correct(correct: C)
  { idx: 10, time: 49000, letter: 'B', correct: false }, // Q11 wrong  (correct: D)
  { idx: 11, time: 56000, letter: 'B', correct: true  }, // Q12 correct(correct: B)
];

// Opponent answers 1 second behind, all wrong
const OPP_ACTIONS = [
  { idx: 4,  time: 8000,  letter: 'C' },
  { idx: 5,  time: 15000, letter: 'A' }, // ← timer cut fires (both done R&W)
  { idx: 6,  time: 22000, letter: 'D' },
  { idx: 7,  time: 29000, letter: 'A' },
  { idx: 8,  time: 36000, letter: 'B' }, // ← timer cut fires (both answered Q9)
  { idx: 9,  time: 43000, letter: 'D' },
  { idx: 10, time: 50000, letter: 'C' },
  { idx: 11, time: 57000, letter: 'A' }, // ← game ends
];

// Timer: starts at 12:00 (720s). Cuts at t=15s → 7:30 (450s), t=36s → 4:00 (240s)
function calcDisplayTime(elapsedMs) {
  const e = elapsedMs / 1000;
  if (e < 15) return Math.max(0, 720 - e);
  if (e < 36) return Math.max(0, 450 - (e - 15));
  return Math.max(0, 240 - (e - 36));
}

function formatTime(s) {
  const sFloor = Math.floor(s);
  const m = Math.floor(sFloor / 60);
  return `${m}:${String(sFloor % 60).padStart(2, '0')}`;
}

// Embedded cursor image (avoids Railway public-folder serving issues)
const CURSOR_IMG = "data:image/png;base64,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";

// ─── GameDemo ─────────────────────────────────────────────────────────────────

// Scripted mouse-cursor path — (x, y) as % of the full card dimensions.
// Topbar ≈ 9% height. Sidebar ≈ 28% width. Question-main: x 28–100%, y 9–100%.
// Passage hover zone: x 40–58%, y 18–38%.
// (CURSOR_PATH removed — cursor now uses live DOM measurements)
const _CURSOR_PATH_REMOVED = [
  // ── Q5 (reading, long passage) → answer A at t=7 000 ms ─────────────────────
  // Button A ≈ y=57%. Stay in button zone, don't go near cameras/topbar.
  { t:     0, x: 52, y: 56 },
  { t:  1800, x: 59, y: 62 },
  { t:  3500, x: 47, y: 58 },
  { t:  5200, x: 56, y: 55 },
  { t:  6500, x: 54, y: 57 },
  { t:  7000, x: 54, y: 57, click: true }, // ← CLICK A
  // ── Q6 (reading, long passage) → answer B at t=14 000 ms ────────────────────
  // Button B ≈ y=68%
  { t:  7700, x: 59, y: 61 },
  { t:  9300, x: 50, y: 67 },
  { t: 11000, x: 58, y: 62 },
  { t: 12700, x: 52, y: 68 },
  { t: 13700, x: 54, y: 68 },
  { t: 14000, x: 54, y: 68, click: true }, // ← CLICK B
  // ── Math Q1 (short: "4x+1=33") → answer A at t=21 000 ms ───────────────────
  // Button A ≈ y=44%
  { t: 14800, x: 58, y: 63 },
  { t: 16400, x: 52, y: 50 },
  { t: 18000, x: 62, y: 46 },
  { t: 19700, x: 55, y: 44 },
  { t: 20800, x: 54, y: 44 },
  { t: 21000, x: 54, y: 44, click: true }, // ← CLICK A
  // ── Math Q2 (short) → answer B at t=28 000 ms ───────────────────────────────
  // Button B ≈ y=53%
  { t: 21800, x: 59, y: 48 },
  { t: 23400, x: 51, y: 55 },
  { t: 25100, x: 62, y: 50 },
  { t: 26800, x: 54, y: 54 },
  { t: 27700, x: 54, y: 53 },
  { t: 28000, x: 54, y: 53, click: true }, // ← CLICK B
  // ── Math Q3 (short) → answer A at t=35 000 ms ───────────────────────────────
  // Button A ≈ y=44%
  { t: 28800, x: 59, y: 50 },
  { t: 30400, x: 52, y: 47 },
  { t: 32000, x: 62, y: 43 },
  { t: 33600, x: 55, y: 47 },
  { t: 34700, x: 54, y: 44 },
  { t: 35000, x: 54, y: 44, click: true }, // ← CLICK A
  // ── Math Q4 (longer passage) → answer C at t=42 000 ms ──────────────────────
  // Button C ≈ y=66%
  { t: 35800, x: 59, y: 51 },
  { t: 37400, x: 52, y: 58 },
  { t: 39100, x: 62, y: 64 },
  { t: 40800, x: 55, y: 68 },
  { t: 41700, x: 54, y: 66 },
  { t: 42000, x: 54, y: 66, click: true }, // ← CLICK C
  // ── Math Q5 (short) → answer B at t=49 000 ms ───────────────────────────────
  // Button B ≈ y=53%
  { t: 42800, x: 59, y: 61 },
  { t: 44400, x: 52, y: 53 },
  { t: 46000, x: 62, y: 57 },
  { t: 47700, x: 55, y: 52 },
  { t: 48700, x: 54, y: 53 },
  { t: 49000, x: 54, y: 53, click: true }, // ← CLICK B
  // ── Math Q6 (short) → answer B at t=56 000 ms ───────────────────────────────
  // Button B ≈ y=52%
  { t: 49800, x: 59, y: 57 },
  { t: 51400, x: 52, y: 54 },
  { t: 53000, x: 62, y: 50 },
  { t: 54700, x: 55, y: 53 },
  { t: 55700, x: 54, y: 52 },
  { t: 56000, x: 54, y: 52, click: true }, // ← CLICK B (game ends)
  { t: 57000, x: 55, y: 50 },
];

// Cursor position from a pre-computed button position cache — zero DOM reads per frame.
// cache        = { A:{x,y}, B:{x,y}, C:{x,y}, D:{x,y} } positions as % of wrapper.
// elapsedMs    = game clock (100ms steps) — used only for discrete action detection.
// rafNow       = performance.now() from RAF callback — used for sine wave + approach easing.
// smoothMs     = rafNow - demoStartRafNow — continuously-updating game-clock estimate.
function computeCursorPos(elapsedMs, rafNow, smoothMs, cache) {
  const getPos = (letter) => cache[letter] || null;

  // Use game clock for discrete events (which action just fired / is next)
  const justClicked = PLAYER_ACTIONS.find(a => {
    const dt = elapsedMs - a.time;
    return dt >= 0 && dt < 400;
  });
  const nextAction  = PLAYER_ACTIONS.find(a => a.time > elapsedMs);
  const hoverAction = nextAction || PLAYER_ACTIONS[PLAYER_ACTIONS.length - 1];
  const hoverPos    = getPos(hoverAction.letter) || { x: 55, y: 55 };

  // Real time for smooth 60fps sine wave drift
  const t      = rafNow / 1000;
  const dx     = Math.sin(t * 0.83) * 5 + Math.sin(t * 1.47) * 2.5;
  const dy     = Math.cos(t * 0.61) * 4 + Math.cos(t * 1.13) * 2;
  const driftX = hoverPos.x + dx;
  const driftY = hoverPos.y + dy;

  if (justClicked) {
    const pos = getPos(justClicked.letter);
    if (pos) return { x: pos.x, y: pos.y, click: (smoothMs - justClicked.time) < 250 };
  }

  if (nextAction) {
    // Use smoothMs (real time) so approach easing is continuous, not 100ms-stepped
    const timeToClick = nextAction.time - smoothMs;
    const APPROACH = 1800;
    if (timeToClick < APPROACH) {
      const pos = getPos(nextAction.letter);
      if (pos) {
        const raw  = 1 - timeToClick / APPROACH;
        const ease = raw < 0.5 ? 4 * raw ** 3 : 1 - (-2 * raw + 2) ** 3 / 2;
        return { x: driftX + (pos.x - driftX) * ease, y: driftY + (pos.y - driftY) * ease, click: false };
      }
    }
  }

  return { x: driftX, y: driftY, click: false };
}

// Measure button positions once and store in cache — called only on question change.
function measureButtonPositions(choicesEl, wrapperEl) {
  const cache = {};
  if (!choicesEl || !wrapperEl) return cache;
  const wr      = wrapperEl.getBoundingClientRect();
  const buttons = choicesEl.querySelectorAll('.choice-btn');
  ['A', 'B', 'C', 'D'].forEach((letter, i) => {
    if (buttons[i]) {
      const r = buttons[i].getBoundingClientRect();
      cache[letter] = {
        x: ((r.left + r.width * 0.38) - wr.left) / wr.width * 100,
        y: ((r.top  + r.height * 0.5)  - wr.top)  / wr.height * 100,
      };
    }
  });
  return cache;
}

// Chat events: triggered when the player answers the given question index
const CHAT_EVENTS = [
  { triggerIdx: 4,  sender: 'opp',    text: 'I take the SAT tomorrow…' },
  { triggerIdx: 5,  sender: 'player', text: 'I scored 780 on math last time. XD' },
  { triggerIdx: 6,  sender: 'opp',    text: "lol I've played 5 ranked games today to practice." },
];

function GameDemo({ onEnd, onCursorUpdate, wrapperRef }) {
  const [playerAnswers, setPlayerAnswers] = useState({ ...PRE_ANSWERS });
  const [oppAnswers, setOppAnswers]       = useState({ ...OPP_PRE_ANSWERS });
  const [displayQ, setDisplayQ]           = useState(4);       // question shown in main panel
  const [oppLastAnswered, setOppLastAnswered] = useState(3);   // opp's last answered index
  const [timeLeft, setTimeLeft]           = useState(720);
  const [timerCutLabel, setTimerCutLabel] = useState(null);
  const [timerCutFlash, setTimerCutFlash] = useState(false);
  const [gameWon, setGameWon]             = useState(false);
  const [chatMessages, setChatMessages]   = useState([]);

  const choicesRef    = useRef(null);
  const elapsedRef    = useRef(0);
  const playerDone    = useRef(new Set());
  const oppDone       = useRef(new Set());
  const cutsDone      = useRef(new Set());
  const chatDone      = useRef(new Set());
  const intervalRef   = useRef(null);
  const rafRef        = useRef(null);
  const cutTimerRef   = useRef(null);
  const endCalledRef  = useRef(false);
  const onEndRef      = useRef(onEnd);
  const onCursorRef   = useRef(onCursorUpdate);
  const chatEndRef    = useRef(null);
  const displayQRef   = useRef(4);   // mirrors displayQ for use inside RAF closure
  const btnCacheRef   = useRef({});  // cached button positions {A:{x,y}, B:{x,y}, ...}
  const cacheForQRef  = useRef(-1);  // which question index the cache was measured for
  useEffect(() => { onEndRef.current = onEnd; }, [onEnd]);
  useEffect(() => { onCursorRef.current = onCursorUpdate; }, [onCursorUpdate]);
  useEffect(() => { displayQRef.current = displayQ; }, [displayQ]);

  // RAF loop — cursor only, runs at ~60 fps independent of game tick.
  // Button positions are measured once per question change, not every frame.
  useEffect(() => {
    let startRafNow = null;
    function loop(rafNow) {
      if (startRafNow === null) startRafNow = rafNow;
      const smoothMs = rafNow - startRafNow; // continuously-updating elapsed estimate

      const q = displayQRef.current;
      if (q !== cacheForQRef.current) {
        btnCacheRef.current = measureButtonPositions(choicesRef.current, wrapperRef?.current);
        cacheForQRef.current = q;
      }
      if (onCursorRef.current) {
        onCursorRef.current(computeCursorPos(elapsedRef.current, rafNow, smoothMs, btnCacheRef.current));
      }
      rafRef.current = requestAnimationFrame(loop);
    }
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []); // eslint-disable-line

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [chatMessages]);

  function showTimerCut(label) {
    setTimerCutFlash(true);
    setTimerCutLabel(label);
    if (cutTimerRef.current) clearTimeout(cutTimerRef.current);
    cutTimerRef.current = setTimeout(() => {
      setTimerCutLabel(null);
      setTimerCutFlash(false);
    }, 3400);
  }

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      elapsedRef.current += 100;
      const elapsed = elapsedRef.current;

      // Only re-render for the timer when the displayed second changes (1x/sec not 10x/sec)
      const newTime = calcDisplayTime(elapsed);
      if (Math.floor(newTime) !== Math.floor(calcDisplayTime(elapsed - 100))) {
        setTimeLeft(newTime);
      }

      // Player actions
      for (const a of PLAYER_ACTIONS) {
        if (!playerDone.current.has(a.idx) && elapsed >= a.time) {
          playerDone.current.add(a.idx);
          const action = a;
          setPlayerAnswers(prev => ({ ...prev, [action.idx]: { letter: action.letter, correct: action.correct } }));
          // Advance displayed question after brief reveal
          if (action.idx < 11) {
            setTimeout(() => setDisplayQ(action.idx + 1), 650);
          }
          // Chat events triggered on player answer
          for (const ce of CHAT_EVENTS) {
            if (ce.triggerIdx === action.idx && !chatDone.current.has(action.idx)) {
              chatDone.current.add(action.idx);
              const ev = ce;
              setTimeout(() => {
                setChatMessages(prev => [...prev, { id: Date.now() + Math.random(), sender: ev.sender, text: ev.text }]);
              }, 400);
            }
          }
        }
      }

      // Opponent actions
      for (const a of OPP_ACTIONS) {
        if (!oppDone.current.has(a.idx) && elapsed >= a.time) {
          oppDone.current.add(a.idx);
          setOppAnswers(prev => ({ ...prev, [a.idx]: { letter: a.letter, correct: false } }));
          setOppLastAnswered(a.idx);

          // Timer cut: both finished all R&W (opp answers Q6/idx 5 at t=15s)
          if (a.idx === 5 && !cutsDone.current.has('rw')) {
            cutsDone.current.add('rw');
            showTimerCut('−4m 15s');
          }
          // Timer cut: both answered Q9/idx 8 at t=36s
          if (a.idx === 8 && !cutsDone.current.has('m3')) {
            cutsDone.current.add('m3');
            showTimerCut('−3m 9s');
          }
          // Game end: opp finishes last question at t=57s
          if (a.idx === 11 && !endCalledRef.current) {
            endCalledRef.current = true;
            clearInterval(intervalRef.current);
            setGameWon(true);
            onEndRef.current();
          }
        }
      }
    }, 100);

    return () => {
      clearInterval(intervalRef.current);
      if (cutTimerRef.current) clearTimeout(cutTimerRef.current);
    };
  }, []); // eslint-disable-line

  const playerScore = Object.values(playerAnswers).filter(a => a?.correct).length;
  const oppScore    = 0;
  const oppPipIdx   = oppLastAnswered + 1; // question opp is currently on

  const timerSecs  = Math.floor(timeLeft);
  const timerClass = timerSecs <= 60 ? 'danger' : timerSecs <= 180 ? 'warning' : '';

  const q          = DEMO_QUESTIONS[displayQ];
  const playerAns  = playerAnswers[displayQ];
  const isRW       = q.section === 'Reading & Writing';

  const playerRank = getRankInfo(PLAYER_BOT.rating);
  const oppRank    = getRankInfo(OPP_BOT.rating);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: 'var(--cb-white)', borderRadius: 12, overflow: 'hidden',
      border: '1px solid var(--cb-gray-300)', boxShadow: 'none',
      position: 'relative',
    }}>

      {/* ── Victory results modal ── */}
      {gameWon && (() => {
        const meRank  = getRankInfo(PLAYER_BOT.rating);
        const oppRank = getRankInfo(OPP_BOT.rating);
        const correctCount = playerScore;
        const breakdown = [
          { type: 'win',       label: 'win',         amount: 50 },
          { type: 'time',      label: 'time played',  amount: 30 },
          { type: 'questions', label: 'questions',    correctCount, amount: correctCount * 25 },
        ];
        const totalXp = breakdown.reduce((s, i) => s + i.amount, 0);
        return (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(8,14,28,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, overflowY: 'auto', padding: '12px 8px',
          }}>
            <div style={{
              background: 'var(--cb-white)', borderRadius: 16,
              padding: '28px 24px 20px', maxWidth: 380, width: '90%',
              textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
              border: '1px solid var(--cb-gray-200)',
              animation: 'modalPop 0.28s cubic-bezier(.34,1.56,.64,1)',
            }}>
              <DemoOutcomeIcon />
              <div style={{ fontSize: 14, color: 'var(--cb-gray-500)', marginBottom: 18 }}>Match complete</div>

              {/* Score row */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20,
                background: 'var(--cb-gray-100)', borderRadius: 12, padding: '18px 16px', marginBottom: 4,
              }}>
                {/* Me (maya_s) */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, flex: 1 }}>
                  <DemoAvatarFallback name={PLAYER_BOT.name} size={44} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, maxWidth: 110, overflow: 'hidden' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0,
                      background: `${meRank.color}20`, border: `1.5px solid ${meRank.color}55`,
                      borderRadius: 5, padding: '1px 5px', fontSize: 10, fontWeight: 800, color: meRank.color,
                    }}>
                      {meRank.level}
                      <img src={meRank.icon} alt={meRank.label} style={{ width: 12, height: 12 }} />
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--cb-gray-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{PLAYER_BOT.name}</span>
                  </div>
                  <div style={{ fontSize: 36, fontWeight: 800, color: 'var(--cb-navy-text)', lineHeight: 1 }}>{playerScore}</div>
                </div>

                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--cb-gray-300)' }}>–</div>

                {/* Opponent (j_torres) */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, flex: 1 }}>
                  <DemoAvatarFallback name={OPP_BOT.name} size={44} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, maxWidth: 110, overflow: 'hidden' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0,
                      background: `${oppRank.color}20`, border: `1.5px solid ${oppRank.color}55`,
                      borderRadius: 5, padding: '1px 5px', fontSize: 10, fontWeight: 800, color: oppRank.color,
                    }}>
                      {oppRank.level}
                      <img src={oppRank.icon} alt={oppRank.label} style={{ width: 12, height: 12 }} />
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--cb-gray-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{OPP_BOT.name}</span>
                  </div>
                  <div style={{ fontSize: 36, fontWeight: 800, color: 'var(--cb-navy-text)', lineHeight: 1 }}>{oppScore}</div>
                </div>
              </div>

              {/* XP breakdown */}
              <DemoXpBreakdown totalXpGained={totalXp} breakdown={breakdown} />

              {/* Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 22 }}>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Sign Up to Play
                </button>
                <div style={{ display: 'flex', gap: 10 }}>
                  <a href="/leaderboard" className="btn btn-ghost" style={{ flex: 1 }}>Leaderboard</a>
                  <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Home</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Topbar ── */}
      <div className="game-topbar">
        <div className="score-panel">

          {/* Player (left) */}
          <div className="score-player score-player-left">
            <div className="score-name-row">
              <div style={{
                width: 26, height: 26, borderRadius: '50%',
                background: playerRank.color + '33', border: `2px solid ${playerRank.color}88`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, color: playerRank.color, flexShrink: 0,
              }}>{PLAYER_BOT.initials}</div>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 2,
                background: `${playerRank.color}22`, border: `1.5px solid ${playerRank.color}55`,
                borderRadius: 5, padding: '1px 5px', fontSize: 10, fontWeight: 800,
                color: playerRank.color, flexShrink: 0,
              }}>
                {playerRank.level}
                <img src={playerRank.icon} alt="" style={{ width: 13, height: 13, objectFit: 'contain' }} />
              </span>
              <span className="score-name" style={{ color: playerRank.color }}>{PLAYER_BOT.name}</span>
            </div>
            <div className="score-school">{PLAYER_BOT.school}</div>
          </div>

          {/* VS + live score */}
          <div className="score-vs-block" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18, fontWeight: 900, color: 'white', minWidth: 18, textAlign: 'right' }}>{playerScore}</span>
              <div className="score-vs">VS</div>
              <span style={{ fontSize: 18, fontWeight: 900, color: 'rgba(255,255,255,.5)', minWidth: 18 }}>{oppScore}</span>
            </div>
          </div>

          {/* Opponent (right) */}
          <div className="score-player score-player-right">
            <div className="score-name-row">
              <span className="score-name" style={{ color: oppRank.color }}>{OPP_BOT.name}</span>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 2,
                background: `${oppRank.color}22`, border: `1.5px solid ${oppRank.color}55`,
                borderRadius: 5, padding: '1px 5px', fontSize: 10, fontWeight: 800,
                color: oppRank.color, flexShrink: 0,
              }}>
                {oppRank.level}
                <img src={oppRank.icon} alt="" style={{ width: 13, height: 13, objectFit: 'contain' }} />
              </span>
              <div style={{
                width: 26, height: 26, borderRadius: '50%',
                background: oppRank.color + '33', border: `2px solid ${oppRank.color}88`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, color: oppRank.color, flexShrink: 0,
              }}>{OPP_BOT.initials}</div>
            </div>
            <div className="score-school">{OPP_BOT.school}</div>
          </div>
        </div>

        {/* Center: timer */}
        <div className="game-topbar-center">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className={`game-timer ${timerClass}${timerCutFlash ? ' timer-cut-flash' : ''}`}>
              {formatTime(timerSecs)}
            </div>
            {timerCutLabel && (
              <span key={timerCutLabel} className="timer-cut-label">{timerCutLabel}</span>
            )}
          </div>
          <div className="game-timer-label">Time Remaining</div>
          <div className="game-progress-bar">
            <div className="game-progress-fill"
              style={{ width: `${Math.max(0, (timerSecs / 900) * 100)}%` }} />
          </div>
        </div>

        {/* Right: answered count */}
        <div style={{ textAlign: 'right', minWidth: 72 }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,.7)' }}>
            {Object.keys(playerAnswers).length}/12
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,.45)', marginTop: 2 }}>
            Q{displayQ + 1} of 12
          </div>
        </div>
      </div>

      {/* ── Game Body ── */}
      <div className="game-body">

        {/* Sidebar */}
        <div className="question-sidebar" style={{ width: 180, minWidth: 180 }}>
          <div className="sidebar-section">Reading &amp; Writing</div>
          <div className="question-nav-grid">
            {Array.from({ length: 6 }, (_, i) => {
              const ans = playerAnswers[i];
              let cls = 'qnav-btn';
              if (displayQ === i) cls += ' active';
              else if (ans !== undefined) cls += ans.correct ? ' answered-correct' : ' answered-wrong';
              return (
                <button key={i} className={cls} style={{ position: 'relative', cursor: 'default' }}>
                  {i + 1}
                  {oppPipIdx === i && (
                    <span style={{
                      position: 'absolute', top: -6, right: -6, width: 15, height: 15,
                      borderRadius: '50%', border: '2px solid white', background: oppRank.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 6, fontWeight: 800, color: 'white',
                      boxShadow: '0 1px 4px rgba(0,0,0,.3)', zIndex: 2,
                    }}>{OPP_BOT.initials}</span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="sidebar-section">Math</div>
          <div className="question-nav-grid">
            {Array.from({ length: 6 }, (_, i) => {
              const idx = i + 6;
              const ans = playerAnswers[idx];
              let cls = 'qnav-btn';
              if (displayQ === idx) cls += ' active';
              else if (ans !== undefined) cls += ans.correct ? ' answered-correct' : ' answered-wrong';
              return (
                <button key={idx} className={cls} style={{ position: 'relative', cursor: 'default' }}>
                  {i + 1}
                  {oppPipIdx === idx && (
                    <span style={{
                      position: 'absolute', top: -6, right: -6, width: 15, height: 15,
                      borderRadius: '50%', border: '2px solid white', background: oppRank.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 6, fontWeight: 800, color: 'white',
                      boxShadow: '0 1px 4px rgba(0,0,0,.3)', zIndex: 2,
                    }}>{OPP_BOT.initials}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Score display */}
          <div style={{
            padding: '10px 12px',
            borderTop: '1px solid var(--cb-gray-200)', fontSize: 11, color: 'var(--cb-gray-500)',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span>{PLAYER_BOT.name}</span>
              <strong style={{ color: 'var(--cb-green)' }}>{playerScore} ✓</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{OPP_BOT.name}</span>
              <strong style={{ color: 'var(--cb-gray-400)' }}>{oppScore} ✓</strong>
            </div>
          </div>

          {/* Match Chat */}
          <div className="match-chat" style={{ marginTop: 0 }}>
            <div className="match-chat-header">Match Chat</div>
            <div className="match-chat-feed">
              {chatMessages.length === 0 ? (
                <div className="match-chat-empty">No messages yet — say hi!</div>
              ) : (
                chatMessages.map(msg => {
                  const isPlayer = msg.sender === 'player';
                  const bot      = isPlayer ? PLAYER_BOT : OPP_BOT;
                  const rankInfo = getRankInfo(bot.rating);
                  return (
                    <div key={msg.id} className="match-chat-msg">
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 2,
                        background: `${rankInfo.color}18`, border: `1px solid ${rankInfo.color}44`,
                        borderRadius: 4, padding: '0 4px', fontSize: 10, fontWeight: 800,
                        color: rankInfo.color, marginRight: 2,
                      }}>
                        {rankInfo.level}
                        <img src={rankInfo.icon} alt={rankInfo.label}
                          style={{ margin: 0, width: 12, height: 12 }} />
                      </span>
                      <span className="match-chat-user" style={{ color: rankInfo.color }}>
                        {bot.name.split(' ')[0]}
                      </span>
                      <span className="match-chat-text">: {msg.text}</span>
                    </div>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </div>
          </div>
        </div>

        {/* Main question area */}
        <div className="question-main" style={{ overflowY: 'auto' }}>
          <div className="question-header">
            <span className="question-number">Question {displayQ + 1}</span>
            <span className={`question-section-badge ${isRW ? '' : 'math'}`}>{q.section}</span>
            {q.domain && <span className="question-domain">{q.domain}</span>}
          </div>

          {q.passage && (
            <div className="passage-box">
              <div className="passage-label">{isRW ? 'Passage' : 'Problem'}</div>
              {q.passage}
            </div>
          )}

          <div className="question-text">{q.question}</div>

          <div className="choices-list" ref={choicesRef}>
            {Object.entries(q.choices).map(([letter, text]) => {
              let cls = '';
              if (playerAns) {
                if (playerAns.letter === letter) {
                  cls = playerAns.correct ? 'correct' : 'incorrect';
                } else if (!playerAns.correct && letter === q.correctAnswer) {
                  cls = 'correct';
                }
              }
              return (
                <button
                  key={letter}
                  className={`choice-btn ${cls}`}
                  disabled
                  style={{ cursor: 'default' }}
                >
                  <span className="choice-label">{letter}</span>
                  <span>{text}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

    </div>
  );
}

// ─── GameDemoWrapper ──────────────────────────────────────────────────────────

function GameDemoWrapper() {
  const wrapperRef                = useRef(null);
  const cursorImgRef              = useRef(null);
  const wrapperSizeRef            = useRef({ width: 0, height: 0 });
  const [demoKey, setDemoKey]     = useState(0);
  const [flashing, setFlashing]   = useState(false);

  // Keep wrapper size cached via ResizeObserver — zero getBoundingClientRect calls per frame
  useEffect(() => {
    const update = () => {
      if (wrapperRef.current) {
        const { width, height } = wrapperRef.current.getBoundingClientRect();
        wrapperSizeRef.current = { width, height };
      }
    };
    update();
    const ro = new ResizeObserver(update);
    if (wrapperRef.current) ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, []);

  const handleCursorUpdate = useCallback((pos) => {
    const el = cursorImgRef.current;
    if (!el) return;
    const { width, height } = wrapperSizeRef.current;
    if (!width) return;
    el.style.transform = `translate(${pos.x / 100 * width}px, ${pos.y / 100 * height}px) scale(${pos.click ? 0.78 : 1})`;
  }, []);

  const handleEnd = useCallback(() => {
    // 2.5 s pause (winner screen visible), then white flash, then restart
    setTimeout(() => {
      setFlashing(true);
      setTimeout(() => {
        setDemoKey(k => k + 1);
        setFlashing(false);
      }, 400);
    }, 2500);
  }, []);

  return (
    // This div has position:relative and NO overflow, so the cursor renders freely
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
      <GameDemo key={demoKey} onEnd={handleEnd} onCursorUpdate={handleCursorUpdate} wrapperRef={wrapperRef} />

      {/* Cursor — embedded PNG via base64 data URL */}
      <img
        ref={cursorImgRef}
        src={CURSOR_IMG}
        alt=""
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: 18,
          height: 'auto',
          pointerEvents: 'none',
          zIndex: 200,
          transform: 'translate(55%, 55%)',
          filter: 'none',
          userSelect: 'none',
          willChange: 'transform',
        }}
      />

      {flashing && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'white', zIndex: 100,
          animation: 'demoFlash 0.4s ease',
          pointerEvents: 'none',
          borderRadius: 12,
        }} />
      )}
    </div>
  );
}

// ─── Auth sub-components ──────────────────────────────────────────────────────

function GoogleButton({ onClick }) {
  return (
    <button className="google-btn" onClick={onClick}>
      <svg className="google-icon" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      Continue with Google
    </button>
  );
}

function Divider() {
  return <div className="login-divider"><span>or</span></div>;
}

function BackButton({ onClick }) {
  return (
    <button type="button" className="back-btn" onClick={onClick}>
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
        <path d="M12 15l-5-5 5-5"/>
      </svg>
      Back
    </button>
  );
}

// ─── Results Modal Helpers (mirrors Game.js) ──────────────────────────────────

function DemoAvatarFallback({ name, size = 44 }) {
  const initial = (name ?? '?').charAt(0).toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'var(--cb-navy)', border: '3px solid var(--cb-gray-200)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.36, fontWeight: 700, color: 'white',
    }}>
      {initial}
    </div>
  );
}

function DemoOutcomeIcon() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto 12px', width: '100%' }}>
      <img src="/Victory_V2.png" alt="Victory" style={{ height: 84, width: 'auto', display: 'block', margin: '0 auto' }} />
    </div>
  );
}

function useDemoCountUp(target, duration = 1400) {
  const [display, setDisplay] = useState(0);
  const frameRef = useRef(null);
  useEffect(() => {
    if (target <= 0) { setDisplay(0); return; }
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * target));
      if (progress < 1) frameRef.current = requestAnimationFrame(step);
    };
    frameRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameRef.current);
  }, [target, duration]);
  return display;
}

function DemoXpBreakdown({ totalXpGained, breakdown }) {
  const animatedTotal = useDemoCountUp(totalXpGained, 1200);
  const itemColor = (item) => {
    if (item.type === 'questions') return '#22c55e';
    return '#4a90e2';
  };
  const itemLabel = (item) => {
    if (item.label === 'win')         return 'Win bonus';
    if (item.label === 'time played') return 'Time played';
    if (item.type === 'questions')    return `${item.correctCount} correct answer${item.correctCount !== 1 ? 's' : ''}`;
    return item.label;
  };
  return (
    <div style={{ paddingTop: 10, borderTop: '1px solid var(--cb-gray-200)', marginTop: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--cb-gray-500)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
        XP Breakdown
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {breakdown.map((item, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
            <span style={{ color: 'var(--cb-gray-600)' }}>{itemLabel(item)}</span>
            <span style={{ fontWeight: 700, color: itemColor(item) }}>+{item.amount} XP</span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: '2px solid var(--cb-gray-200)' }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--cb-gray-700)' }}>Total XP</span>
        <span style={{ fontSize: 19, fontWeight: 900, color: '#4a90e2', fontVariantNumeric: 'tabular-nums' }}>
          +{animatedTotal} XP
        </span>
      </div>
    </div>
  );
}

// ─── Leaderboard Preview Card ─────────────────────────────────────────────────

// icon = image src, color = text + border color
const LB_RANK_CONFIG = {
  'LIFETIME XP':  { color: 'var(--cb-navy)' },
  'DAILY WINS':   { color: '#3c8cdc' },
  'LOGIN STREAK': { color: 'var(--cb-navy)' },
};

function LbPreviewCard({ title, players, renderMetric, loading }) {
  const top10 = (players || []).slice(0, 10);
  const rankCfg = LB_RANK_CONFIG[title] || null;
  return (
    <div className="lb-block">
      <div className="lb-block-header" style={{ paddingBottom: 7 }}>
        {rankCfg ? (
          <span className="lb-block-title" style={{
            fontFamily: "'Impact', 'Arial Narrow', sans-serif",
            fontSize: 38, fontWeight: 400, color: rankCfg.color,
          }}>
            {title}
          </span>
        ) : (
          <span className="lb-block-title">{title}</span>
        )}
      </div>
      <div className="lb-block-body">
        {loading ? (
          <div className="lb-empty" style={{ padding: '24px' }}>
            <div className="queue-spinner" style={{ margin: '0 auto 10px' }} />
            Loading…
          </div>
        ) : top10.length === 0 ? (
          <div className="lb-empty" style={{ padding: '24px' }}>No players yet.</div>
        ) : (
          top10.map((p, i) => {
            const rankInfo = getRankInfo(p.rating ?? 0);
            const displayName = p.display_name || p.name;
            return (
              <div key={p.id} className="lb-compact-row">
                <div className={`lb-rank-num${i < 3 ? ' top' + (i + 1) : ''}`}>{i + 1}</div>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--cb-navy)', border: '2px solid var(--cb-gray-200)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'white', flexShrink: 0 }}>
                  {p.avatar
                    ? <img src={p.avatar} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover' }} />
                    : (displayName ?? '?').charAt(0).toUpperCase()
                  }
                </div>
                <div className="lb-compact-info">
                  <div className="lb-compact-name" style={{ color: rankInfo.color, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 2,
                      background: `${rankInfo.color}18`, border: `1.2px solid ${rankInfo.color}44`,
                      borderRadius: 5, padding: '1px 4px', fontSize: 10, fontWeight: 800, color: rankInfo.color, flexShrink: 0,
                    }}>
                      {rankInfo.level}
                      <img src={rankInfo.icon} alt={rankInfo.label} style={{ width: 13, height: 13, objectFit: 'contain' }} />
                    </span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{displayName}</span>
                  </div>
                  {p.school && <div className="lb-compact-school">{p.school}</div>}
                </div>
                <div className="lb-compact-metric">{renderMetric(p)}</div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Main Login Page ──────────────────────────────────────────────────────────

export default function Login() {
  const { user, loading } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate  = useNavigate();
  const location  = useLocation();
  const lbRef     = useRef(null);

  useEffect(() => {
    if (!loading && user) {
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate]);

  const searchParams = new URLSearchParams(location.search);
  const urlError     = searchParams.get('error');
  const resetSuccess = searchParams.get('reset') === '1';

  // Show GET STARTED vs auth card
  const [showAuth, setShowAuth] = useState(urlError === 'google_exists');

  const [mode, setMode] = useState(urlError === 'google_exists' ? 'signup' : 'login');
  const [onlineCount, setOnlineCount]   = useState(null);
  const [matchesToday, setMatchesToday] = useState(null);

  useEffect(() => {
    const fetchStats = () => {
      axios.get(`${API}/api/online-count`).then(r => setOnlineCount(r.data.count)).catch(() => {});
      axios.get(`${API}/api/matches-today`).then(r => setMatchesToday(r.data.count)).catch(() => {});
    };
    fetchStats();
    const iv = setInterval(fetchStats, 30000);
    return () => clearInterval(iv);
  }, []);

  // Leaderboard preview
  const [lbXp,      setLbXp]      = useState([]);
  const [lbWins,    setLbWins]    = useState([]);
  const [lbStreaks, setLbStreaks] = useState([]);
  const [lbLoading, setLbLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/leaderboard`).then(r => setLbXp(r.data || [])).catch(() => {}),
      axios.get(`${API}/api/leaderboard/daily`).then(r => setLbWins(r.data || [])).catch(() => {}),
      axios.get(`${API}/api/leaderboard/streaks`).then(r => setLbStreaks(r.data || [])).catch(() => {}),
    ]).finally(() => setLbLoading(false));
  }, []);

  // Google auth — Supabase handles the OAuth flow
  const handleGoogleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) console.error('[Google OAuth]', error.message);
  };

  // ── Login state ──────────────────────────────────────────────────────────────
  const [loginId, setLoginId]             = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError]       = useState('');
  const [loginLoading, setLoginLoading]   = useState(false);
  const [showLoginPw, setShowLoginPw]     = useState(false);
  const [loginLockedFor, setLoginLockedFor] = useState(0);
  const lockdownRef = useRef(null);

  function startLockdown(secs) {
    setLoginLockedFor(secs);
    clearInterval(lockdownRef.current);
    lockdownRef.current = setInterval(() => {
      setLoginLockedFor(s => {
        if (s <= 1) { clearInterval(lockdownRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  }
  useEffect(() => () => clearInterval(lockdownRef.current), []);

  // ── Signup state ─────────────────────────────────────────────────────────────
  const [signupStep, setSignupStep]         = useState('details');
  const [signupEmail, setSignupEmail]       = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirm, setSignupConfirm]   = useState('');
  const [showSignupPw, setShowSignupPw]     = useState(false);
  const [signupUsername, setSignupUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState(null);
  const debounceRef = useRef(null);
  const [signupCode, setSignupCode]         = useState('');
  const [signupLoading, setSignupLoading]   = useState(false);
  const [signupError, setSignupError]       = useState('');
  const [devCode, setDevCode]               = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess]   = useState(false);
  const cooldownRef = useRef(null);

  function startResendCooldown(secs = 60) {
    setResendCooldown(secs);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(s => {
        if (s <= 1) { clearInterval(cooldownRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  }
  useEffect(() => {
    if (signupStep === 'verify') startResendCooldown(60);
    return () => clearInterval(cooldownRef.current);
  }, [signupStep]); // eslint-disable-line

  // Username availability check
  useEffect(() => {
    const u = signupUsername.trim();
    if (!u) { setUsernameStatus(null); return; }
    if (u.length < 2) { setUsernameStatus({ error: 'At least 2 characters.' }); return; }
    if (u.length > 16) { setUsernameStatus({ error: 'Max 16 characters.' }); return; }
    if (!/^[a-zA-Z0-9_.-]+$/.test(u)) { setUsernameStatus({ error: 'Letters, numbers, _ . - only.' }); return; }
    setUsernameStatus('checking');
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/auth/check-username`, { params: { username: u } });
        setUsernameStatus(res.data.available ? 'available' : { error: res.data.error || 'Username already taken.' });
      } catch {
        setUsernameStatus({ error: 'Could not check username.' });
      }
    }, 400);
  }, [signupUsername]);

  // ── Auth handlers ────────────────────────────────────────────────────────────

  async function handleLogin(e) {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      // Resolve username → email if needed (Supabase only supports email login)
      let email = loginId.trim();
      if (!email.includes('@')) {
        const res = await axios.get(`${API}/auth/lookup-email`, { params: { username: email } });
        email = res.data.email;
      }
      const { error } = await supabase.auth.signInWithPassword({ email, password: loginPassword });
      if (error) {
        setLoginError(error.message === 'Invalid login credentials'
          ? 'Invalid email or password.'
          : error.message);
        return;
      }
      // AuthContext's onAuthStateChange will fire, sync user, and update state.
      // Navigate after a short tick to let the context settle.
      setTimeout(() => navigate('/', { replace: true }), 50);
    } catch (err) {
      setLoginError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  }

  async function handleDetailsNext(e) {
    e.preventDefault();
    setSignupError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupEmail.trim())) { setSignupError('Enter a valid email address.'); return; }
    if (signupPassword.length < 8) { setSignupError('Password must be at least 8 characters.'); return; }
    if (signupPassword !== signupConfirm) { setSignupError('Passwords do not match.'); return; }
    setSignupLoading(true);
    try {
      const res = await axios.get(`${API}/auth/check-email`, { params: { email: signupEmail.trim() } });
      if (!res.data.available) { setSignupError('An account with that email already exists. Please log in.'); return; }
      setSignupStep('username');
    } catch {
      setSignupError('Could not verify email. Please try again.');
    } finally {
      setSignupLoading(false);
    }
  }

  function handleUsernameNext(e) {
    e.preventDefault();
    setSignupError('');
    const u = signupUsername.trim();
    if (!u || usernameStatus !== 'available') { setSignupError('Choose an available username first.'); return; }
    setSignupStep('send');
  }

  async function handleSendVerification(e) {
    e?.preventDefault();
    setSignupError('');
    setSignupLoading(true);
    try {
      const { error } = await supabase.auth.signUp({
        email: signupEmail.trim(),
        password: signupPassword,
        options: {
          data: { username: signupUsername.trim() },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) { setSignupError(error.message); return; }
      setSignupStep('verify');
    } catch (err) {
      setSignupError('Failed to send link. Try again.');
    } finally {
      setSignupLoading(false);
    }
  }

  async function handleResend() {
    if (resendCooldown > 0 || signupLoading) return;
    setResendSuccess(false);
    setSignupError('');
    setSignupLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: signupEmail.trim(),
        options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) { setSignupError(error.message); return; }
      setSignupCode('');
      setResendSuccess(true);
      startResendCooldown(60);
    } catch {
      setSignupError('Failed to resend code. Try again.');
    } finally {
      setSignupLoading(false);
    }
  }

  function switchMode(m) {
    setMode(m);
    setLoginError(''); setSignupError('');
    setSignupStep('details'); setDevCode('');
    setSignupCode(''); setSignupUsername(''); setUsernameStatus(null);
    setSignupPassword(''); setSignupConfirm('');
    setResendCooldown(0); setResendSuccess(false);
    clearInterval(cooldownRef.current);
  }

  function usernameHint() {
    if (!signupUsername.trim()) return null;
    if (usernameStatus === 'checking') return <span className="username-hint checking">Checking…</span>;
    if (usernameStatus === 'available') return <span className="username-hint available">✓ Available</span>;
    if (usernameStatus?.error) return <span className="username-hint taken">{usernameStatus.error}</span>;
    return null;
  }

  function scrollToLb() {
    lbRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ── Auth card inner content ──────────────────────────────────────────────────

  const authInner = (
    <>
      <div className="auth-mode-toggle">
        <button type="button" className={`auth-mode-btn${mode === 'login' ? ' active' : ''}`}
          onClick={() => switchMode('login')}>Log In</button>
        <button type="button" className={`auth-mode-btn${mode === 'signup' ? ' active' : ''}`}
          onClick={() => switchMode('signup')}>Sign Up</button>
      </div>

      {mode === 'signup' && (() => {
        const stepNum = { details: 1, username: 2, send: 3, verify: 3 }[signupStep] ?? 1;
        const labels = ['Details', 'Username', 'Verify'];
        return (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18, marginTop: 14, gap: 0 }}>
            {labels.map((label, i) => {
              const n = i + 1;
              const done = stepNum > n;
              const active = stepNum === n;
              return (
                <React.Fragment key={n}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: '0 0 auto' }}>
                    <div style={{
                      width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700,
                      background: done ? 'var(--cb-navy)' : active ? 'var(--cb-navy)' : 'var(--cb-gray-200)',
                      color: done || active ? '#fff' : 'var(--cb-gray-400)',
                      transition: 'background 0.2s',
                    }}>
                      {done ? '✓' : n}
                    </div>
                    <span style={{ fontSize: 10, fontWeight: active ? 700 : 500, color: active ? 'var(--cb-navy)' : done ? 'var(--cb-gray-500)' : 'var(--cb-gray-400)', whiteSpace: 'nowrap' }}>
                      {label}
                    </span>
                  </div>
                  {i < labels.length - 1 && (
                    <div style={{ flex: 1, height: 2, background: stepNum > n ? 'var(--cb-navy)' : 'var(--cb-gray-200)', marginBottom: 14, transition: 'background 0.2s' }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        );
      })()}

      {resetSuccess && mode === 'login' && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#166534', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>✓</span> Password updated — you can now log in with your new password.
        </div>
      )}

      {/* ── LOGIN ── */}
      {mode === 'login' && (
        <>
          <form className="email-form" onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">Email or username</label>
              <input type="text" className="form-input" placeholder="Email or username"
                value={loginId} onChange={e => setLoginId(e.target.value)} autoFocus required />
            </div>
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <label className="form-label" style={{ margin: 0 }}>Password</label>
                <Link to="/forgot-password" style={{ fontSize: 12, color: 'var(--cb-navy)', fontWeight: 600, textDecoration: 'none' }}>
                  Forgot password?
                </Link>
              </div>
              <div className="pw-input-wrap">
                <input type={showLoginPw ? 'text' : 'password'} className="form-input" placeholder="••••••••"
                  value={loginPassword} onChange={e => setLoginPassword(e.target.value)} required />
                <button type="button" className="pw-toggle" onClick={() => setShowLoginPw(v => !v)} tabIndex={-1}>
                  {showLoginPw ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>
            {loginError && <div className="form-error">{loginError}</div>}
            {loginLockedFor > 0 && (
              <div style={{ fontSize: 12, color: 'var(--cb-gray-400)', textAlign: 'center', marginBottom: 4 }}>
                Locked — try again in {Math.floor(loginLockedFor / 60)}:{String(loginLockedFor % 60).padStart(2, '0')}
              </div>
            )}
            <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 4 }}
              disabled={loginLoading || loginLockedFor > 0}>
              {loginLoading ? 'Signing in…' : loginLockedFor > 0 ? 'Locked' : 'Log In'}
            </button>
          </form>
          <Divider />
          <GoogleButton onClick={handleGoogleLogin} />
        </>
      )}

      {/* ── SIGN UP step 1 ── */}
      {mode === 'signup' && signupStep === 'details' && (
        <>
          <form className="email-form" onSubmit={handleDetailsNext}>
            <div className="form-group">
              <label className="form-label">Enter your email</label>
              <input type="email" className="form-input" placeholder="you@example.com"
                value={signupEmail} onChange={e => setSignupEmail(e.target.value)} autoFocus required />
            </div>
            <div className="form-group">
              <label className="form-label">Create a password</label>
              <div className="pw-input-wrap">
                <input type={showSignupPw ? 'text' : 'password'} className="form-input" placeholder="At least 8 characters"
                  value={signupPassword} onChange={e => setSignupPassword(e.target.value)} required />
                <button type="button" className="pw-toggle" onClick={() => setShowSignupPw(v => !v)} tabIndex={-1}>
                  {showSignupPw ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Confirm password</label>
              <input type={showSignupPw ? 'text' : 'password'} className="form-input" placeholder="Re-enter password"
                value={signupConfirm} onChange={e => setSignupConfirm(e.target.value)} required />
            </div>
            {signupError && <div className="form-error">{signupError}</div>}
            <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 4 }} disabled={signupLoading}>
              {signupLoading ? 'Checking…' : 'Continue'}
            </button>
          </form>
          <Divider />
          {urlError === 'google_exists' && (
            <div className="form-error" style={{ marginBottom: 8 }}>
              An account with that Google address already exists.{' '}
              <button type="button" style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}
                onClick={() => { searchParams.delete('error'); navigate('/login', { replace: true }); setMode('login'); }}>
                Sign in instead?
              </button>
            </div>
          )}
          <GoogleButton onClick={handleGoogleLogin} />
        </>
      )}

      {/* ── SIGN UP step 2 ── */}
      {mode === 'signup' && signupStep === 'username' && (
        <form className="email-form" onSubmit={handleUsernameNext}>
          <div className="email-form-header">
            <BackButton onClick={() => { setSignupStep('details'); setSignupError(''); }} />
            <p className="email-form-title">Choose your username</p>
          </div>
          <p className="email-form-hint">This is how other players will see you. You can change it later.</p>
          <div className="form-group">
            <label className="form-label">Username</label>
            <input type="text" className="form-input" placeholder="e.g. mathwiz99"
              value={signupUsername}
              onChange={e => setSignupUsername(e.target.value.replace(/ /g, '_'))}
              onKeyDown={e => { if (e.key === ' ') { e.preventDefault(); setSignupUsername(v => (v + '_').slice(0, 16)); } }}
              maxLength={16} autoFocus required />
            <div style={{ marginTop: 5 }}>{usernameHint()}</div>
          </div>
          {signupError && <div className="form-error">{signupError}</div>}
          <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 4 }}
            disabled={usernameStatus !== 'available'}>
            Continue
          </button>
        </form>
      )}

      {/* ── SIGN UP step 3 ── */}
      {mode === 'signup' && signupStep === 'send' && (
        <form className="email-form" onSubmit={handleSendVerification}>
          <div className="email-form-header">
            <BackButton onClick={() => { setSignupStep('username'); setSignupError(''); }} />
            <p className="email-form-title">Verify your email</p>
          </div>
          <p className="email-form-hint">
            We'll send a confirmation link to <strong>{signupEmail}</strong>.
          </p>
          {signupError && <div className="form-error">{signupError}</div>}
          <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 4 }}
            disabled={signupLoading}>
            {signupLoading ? 'Sending…' : 'Send Verification Link'}
          </button>
        </form>
      )}

      {/* ── SIGN UP step 4 (waiting) ── */}
      {mode === 'signup' && signupStep === 'verify' && (
        <div className="email-form">
          <div className="email-form-header">
            <BackButton onClick={() => { setSignupStep('send'); setSignupError(''); }} />
            <p className="email-form-title">Check your email</p>
          </div>
          <p className="email-form-hint">
            We sent a confirmation link to <strong>{signupEmail}</strong>. Click it to finish creating your account.
          </p>
          {signupError && <div className="form-error">{signupError}</div>}
          {resendSuccess && !signupError && (
            <div style={{ fontSize: 13, color: 'var(--cb-navy)', marginBottom: 8, textAlign: 'center' }}>
              ✓ New link sent to {signupEmail}
            </div>
          )}
          <div style={{ textAlign: 'center', marginTop: 14, fontSize: 13, color: 'var(--cb-gray-500)' }}>
            Didn't get it?{' '}
            {resendCooldown > 0
              ? <span>Resend in {resendCooldown}s</span>
              : <button type="button" style={{ background: 'none', border: 'none', padding: 0, color: 'var(--cb-navy)', cursor: 'pointer', fontSize: 'inherit', fontWeight: 600 }}
                  onClick={handleResend} disabled={signupLoading}>Resend link</button>
            }
          </div>
        </div>
      )}
    </>
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="lp-shell" style={{ background: 'var(--cb-bg)' }}>

      {/* ── Top Navbar ── */}
      <nav className="navbar lp-topnav" style={{ position: 'relative', zIndex: 10 }}>
        <Link to="/" className="navbar-brand">
          <img src="/RANKEDSATLOGO.png" alt="RankedSAT" style={{ height: 'auto', width: 160, objectFit: 'contain', display: 'block' }} />
        </Link>
        <div style={{ display: 'flex', gap: 4 }}>
          <button className="navbar-link" style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }} onClick={scrollToLb}>Leaderboard</button>
          <Link to="/about" className="navbar-link">About</Link>
        </div>
        <div className="navbar-right">
          <button className="btn btn-ghost btn-sm navbar-theme-btn" onClick={toggleDarkMode}
            title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
            {darkMode ? <SunIcon /> : <MoonIcon />}
          </button>
          <a className="btn btn-ghost btn-sm"
            href="https://docs.google.com/forms/d/e/1FAIpQLSdnJtO6X66IImNTmIVCOPmgIqhssT5Nx-Jgpvhsbu9QNuIRrw/viewform?usp=publish-editor"
            target="_blank" rel="noopener noreferrer"
            style={{ fontWeight: 600, color: 'rgba(255,255,255,.85)' }}>
            Help
          </a>
        </div>
      </nav>

      {/* ── Hero: split left/right ── */}
      <div style={{
        display: 'flex', height: 'calc(90vh - 64px)', overflow: 'hidden',
        background: 'var(--cb-bg)',
        paddingLeft: '5%',
        paddingRight: '5%',
      }}>

        {/* ── Left: Game Demo ── */}
        <div className="lp-demo-panel" style={{
          flex: 1, minWidth: 0,
          padding: '20px 16px 20px 20px',
          overflow: 'visible',
          display: 'flex', flexDirection: 'column',
          background: 'var(--cb-gray-100)',
        }}>
          <div style={{ flex: 1, overflow: 'visible' }}>
            <GameDemoWrapper />
          </div>
        </div>

        {/* ── Right: Logo + Auth ── */}
        <div style={{
          width: 440, minWidth: 440, maxWidth: 440, flexShrink: 0, flexGrow: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '32px 24px',
          background: 'var(--cb-bg)',
          borderLeft: '1px solid var(--cb-gray-200)',
          overflowY: 'auto',
        }}>
          <div style={{ width: '100%', maxWidth: 396, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>

            {/* Logo */}
            <img src="/RankedsatLOGONEW.png" alt="RankedSAT" className="lp-logo" style={{ marginBottom: 6 }} />

            {/* Tagline */}
            <p className="lp-tagline" style={{ textAlign: 'center', marginBottom: 8 }}>
              Compete to answer SAT questions 1v1 against players across the world!
            </p>

            {/* Online stats */}
            {(onlineCount !== null || matchesToday !== null) && (
              <p style={{ fontSize: 12, color: 'var(--cb-gray-400)', textAlign: 'center', marginBottom: 16, fontWeight: 500 }}>
                {[
                  onlineCount   !== null && `${onlineCount} players online`,
                  matchesToday  !== null && `${matchesToday} games today`,
                ].filter(Boolean).join(' · ')}
              </p>
            )}

            {/* GET STARTED button or Auth card */}
            {!showAuth ? (
              <button
                className="lp-get-started-btn"
                onClick={() => setShowAuth(true)}
                style={{ marginTop: 8 }}
              >
                GET STARTED
              </button>
            ) : (
              <div className="lp-auth-card lp-auth-card-enter" style={{ width: '100%', marginTop: 8 }}>
                {authInner}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Below-hero sections ── */}
      <div className="lp-landing-below" style={{ background: 'var(--cb-bg)', position: 'relative', zIndex: 2 }}>

        <div className="lp-landing-section">
          <div className="home-info" style={{ marginTop: 0 }}>
            <div className="info-card">
              <h3>How It Works</h3>
              <p>Match with a player near your rating. Answer 6 Reading &amp; Writing questions and 6 Math questions in 15 minutes. The highest score wins.</p>
            </div>
            <div className="info-card">
              <h3>XP &amp; Ranks</h3>
              <p>Earn XP every match — for time played, wins, and completing all questions. XP never decreases. Climb from Bronze all the way to Grandmaster.</p>
            </div>
          </div>
        </div>

        <div className="lp-landing-section" ref={lbRef}>
          <h2 className="lp-section-title">Leaderboards</h2>
          <div className="lb-three-col">
            <LbPreviewCard
              title="LIFETIME XP"
              players={lbXp}
              loading={lbLoading}
              renderMetric={p => (
                <span className="lb-metric-wins">
                  {(p.rating ?? 0).toLocaleString()} <span className="lb-metric-label">XP</span>
                </span>
              )}
            />
            <LbPreviewCard
              title="DAILY WINS"
              players={lbWins}
              loading={lbLoading}
              renderMetric={p => (
                <span className="lb-metric-wins">
                  {p.win_count} <span className="lb-metric-label">{parseInt(p.win_count) === 1 ? 'win' : 'wins'}</span>
                </span>
              )}
            />
            <LbPreviewCard
              title="LOGIN STREAK"
              players={lbStreaks}
              loading={lbLoading}
              renderMetric={p => (
                <span className="lb-metric-wins">
                  {p.login_streak ?? 0} <span className="lb-metric-label">days</span>
                </span>
              )}
            />
          </div>
        </div>

      </div>

    </div>
  );
}
